import DocumentService from '@/services/document-service';
import type { DocumentResponseType, DocumentUpdateType } from '@/types/doc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

// Query keys
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (filters: any) => [...documentKeys.lists(), filters] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
};

// Query Hooks
export const useDocuments = (filters?: any) => {
  return useQuery<DocumentResponseType>({
    queryKey: documentKeys.list(filters),
    queryFn: async () => {
      const response = await DocumentService.getDocuments(filters);
      return response as DocumentResponseType;
    },
  });
};

export const useDocument = (id: string | undefined) => {
  return useQuery<DocumentResponseType>({
    queryKey: documentKeys.detail(id!),
    queryFn: async () => {
      const response = await DocumentService.getDocumentsById(id!);
      return response as DocumentResponseType;
    },
    enabled: !!id,
  });
};

interface UseCreateOrUpdateDocumentOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useCreateOrUpdateDocument = ({
  onSuccess,
  successMessage,
}: UseCreateOrUpdateDocumentOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id?: string;
      data: Partial<DocumentUpdateType>;
    }) => {
      if (id) {
        return DocumentService.updateDocuments(Number(id), data);
      }
      return DocumentService.createDocuments(data);
    },
    onSuccess: (_, variables) => {
      const message =
        successMessage ??
        (variables.id
          ? 'Document updated successfully'
          : 'Document created successfully');
      toast.success(message, { position: 'top-right' });

      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: documentKeys.detail(variables.id),
        });
      }
      onSuccess?.();
    },
    onError: () => {
      toast.error('Failed to perform operation. Please try again.');
    },
  });
};

interface UseDeleteDocumentOptions {
  onSuccess?: () => void;
  successMessage?: string;
  onError?: () => void;
  onSettled?: () => void;
}

export const useDeleteDocument = ({
  onSuccess,
  successMessage,
  onError,
  onSettled,
}: UseDeleteDocumentOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DocumentService.deleteDocuments(id),
    onSuccess: (_, id) => {
      toast.success(successMessage ?? 'Document deleted successfully', {
        position: 'top-right',
      });
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error('Failed to delete document. Please try again.');
      console.error('Failed to delete document:', error);
      onError?.();
    },
    onSettled: () => {
      onSettled?.();
    },
  });
};
